import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import i18n from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = 'fuse_auth_token';
const USER_KEY = 'fuse_user_data';
const COURSE_KEY = 'fuse_selected_course';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// In-memory fallback storage for environments where localStorage/sessionStorage are blocked
const memoryStorage = {
  data: {},
  getItem(key) {
    return this.data[key] || null;
  },
  setItem(key, value) {
    this.data[key] = value;
  },
  removeItem(key) {
    delete this.data[key];
  }
};

// Detect if storage is available (iOS in-app browsers may block it entirely)
const isStorageAvailable = (type) => {
  try {
    const storage = type === 'local' ? window.localStorage : window.sessionStorage;
    const testKey = '__storage_test__';
    storage.setItem(testKey, testKey);
    storage.removeItem(testKey);
    return true;
  } catch (e) {
    console.warn(`${type}Storage not available:`, e?.message || e);
    return false;
  }
};

// Cache storage availability check results
let localStorageAvailable = null;
let sessionStorageAvailable = null;

const checkLocalStorage = () => {
  if (localStorageAvailable === null) {
    localStorageAvailable = isStorageAvailable('local');
  }
  return localStorageAvailable;
};

const checkSessionStorage = () => {
  if (sessionStorageAvailable === null) {
    sessionStorageAvailable = isStorageAvailable('session');
  }
  return sessionStorageAvailable;
};

// Safe localStorage functions with in-memory fallback
const safeGetItem = (key) => {
  try {
    if (checkLocalStorage()) {
      return localStorage.getItem(key);
    }
    return memoryStorage.getItem(key);
  } catch (e) {
    console.warn('localStorage access error:', e?.message || e);
    return memoryStorage.getItem(key);
  }
};

const safeSetItem = (key, value) => {
  try {
    memoryStorage.setItem(key, value); // Always save to memory as backup
    if (checkLocalStorage()) {
      localStorage.setItem(key, value);
      return true;
    }
    return true; // Stored in memory
  } catch (e) {
    console.warn('localStorage write error:', e?.message || e);
    return true; // Still stored in memory
  }
};

const safeRemoveItem = (key) => {
  try {
    memoryStorage.removeItem(key);
    if (checkLocalStorage()) {
      localStorage.removeItem(key);
    }
  } catch (e) {
    console.warn('localStorage remove error:', e?.message || e);
  }
};

// Session storage functions with in-memory fallback
const safeSessionGetItem = (key) => {
  try {
    if (checkSessionStorage()) {
      return sessionStorage.getItem(key);
    }
    return memoryStorage.getItem(key);
  } catch (e) {
    return memoryStorage.getItem(key);
  }
};

const safeSessionSetItem = (key, value) => {
  try {
    memoryStorage.setItem(key, value); // Always save to memory as backup
    if (checkSessionStorage()) {
      sessionStorage.setItem(key, value);
      return true;
    }
    return true;
  } catch (e) {
    return true;
  }
};

// Get token from any available storage
const getStoredToken = () => {
  try {
    return safeGetItem(TOKEN_KEY) || safeSessionGetItem(TOKEN_KEY) || memoryStorage.getItem(TOKEN_KEY);
  } catch (e) {
    console.warn('Error getting stored token:', e?.message || e);
    return memoryStorage.getItem(TOKEN_KEY);
  }
};

// Get user from any available storage
const getStoredUser = () => {
  try {
    const userData = safeGetItem(USER_KEY) || safeSessionGetItem(USER_KEY) || memoryStorage.getItem(USER_KEY);
    if (userData) {
      try {
        return JSON.parse(userData);
      } catch (parseError) {
        console.warn('Error parsing user data:', parseError?.message || parseError);
        return null;
      }
    }
    return null;
  } catch (e) {
    console.warn('Error getting stored user:', e?.message || e);
    return null;
  }
};

// Save token and user to all available storages
const saveAuthData = (token, user) => {
  try {
    if (token) {
      safeSetItem(TOKEN_KEY, token);
      safeSessionSetItem(TOKEN_KEY, token);
    }
    if (user) {
      const userData = JSON.stringify(user);
      safeSetItem(USER_KEY, userData);
      safeSessionSetItem(USER_KEY, userData);
    }
  } catch (e) {
    console.warn('Error saving auth data:', e?.message || e);
    // Still try to save to memory
    if (token) memoryStorage.setItem(TOKEN_KEY, token);
    if (user) memoryStorage.setItem(USER_KEY, JSON.stringify(user));
  }
};

// Clear all auth data
const clearAuthData = () => {
  try {
    safeRemoveItem(TOKEN_KEY);
    safeRemoveItem(USER_KEY);
    memoryStorage.removeItem(TOKEN_KEY);
    memoryStorage.removeItem(USER_KEY);
    if (checkSessionStorage()) {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
    }
  } catch (e) {
    console.warn('Error clearing auth data:', e?.message || e);
    memoryStorage.removeItem(TOKEN_KEY);
    memoryStorage.removeItem(USER_KEY);
  }
};

export const AuthProvider = ({ children }) => {
  // Use lazy initialization to prevent errors during initial render
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Flag to prevent race conditions during login - critical for iOS
  const [authInProgress, setAuthInProgress] = useState(false);
  const initialized = useRef(false);
  // Ref to track if auth has been validated this session
  const authValidated = useRef(false);
  // Ref to prevent multiple simultaneous auth checks
  const checkingAuth = useRef(false);

  // Initialize auth state safely after component mounts
  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      try {
        const storedUser = getStoredUser();
        const storedToken = getStoredToken();
        console.log('Auth initialized - Token exists:', !!storedToken, 'User exists:', !!storedUser);
        if (storedUser && storedToken) {
          // If we have both token and user, set authenticated immediately
          // This prevents the race condition on iOS where the component renders
          // before state is updated
          setUser(storedUser);
          setToken(storedToken);
          setIsAuthenticated(true);
          authValidated.current = true;
          setLoading(false);
        } else if (storedToken) {
          setToken(storedToken);
          // Don't set loading to false yet - will be set after checkAuth
        } else {
          setLoading(false);
        }
      } catch (e) {
        console.warn('Error initializing auth state:', e?.message || e);
        setLoading(false);
      }
    }
  }, []);

  // Create axios instance with current token
  const getApi = useCallback(() => {
    let currentToken = token;
    try {
      currentToken = token || getStoredToken();
    } catch (e) {
      console.warn('Error getting token for API:', e?.message || e);
    }
    return axios.create({
      baseURL: API_URL,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        ...(currentToken ? { 'Authorization': `Bearer ${currentToken}` } : {}),
      },
    });
  }, [token]);

  // Check auth status
  const checkAuth = useCallback(async () => {
    // Prevent multiple simultaneous auth checks
    if (checkingAuth.current) {
      console.log('Auth check already in progress, skipping');
      return isAuthenticated;
    }
    
    // If auth is already validated this session, skip re-validation to prevent loops
    if (authValidated.current && isAuthenticated) {
      console.log('Auth already validated, skipping re-check');
      setLoading(false);
      return true;
    }
    
    checkingAuth.current = true;
    
    let currentToken = token;
    let cachedUser = user;
    
    try {
      currentToken = token || getStoredToken();
      cachedUser = user || getStoredUser();
    } catch (e) {
      console.warn('Error reading auth cache:', e?.message || e);
    }
    
    console.log('CheckAuth - Token exists:', !!currentToken, 'Cached user:', !!cachedUser);
    
    // If we have cached user data and token, trust it initially
    if (cachedUser && currentToken) {
      setUser(cachedUser);
      setToken(currentToken);
      setIsAuthenticated(true);
      authValidated.current = true;
      setLoading(false);
      checkingAuth.current = false;
      
      // Verify in background without affecting UI state
      try {
        const api = getApi();
        const response = await api.get('/api/auth/me');
        setUser(response.data);
        saveAuthData(currentToken, response.data);
      } catch (error) {
        console.log('Background auth check failed, using cached data');
      }
      return true;
    }
    
    if (!currentToken) {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      checkingAuth.current = false;
      return false;
    }

    try {
      const api = getApi();
      const response = await api.get('/api/auth/me');
      console.log('Auth check successful');
      setUser(response.data);
      saveAuthData(currentToken, response.data);
      setIsAuthenticated(true);
      authValidated.current = true;
      return true;
    } catch (error) {
      console.error('Auth check failed:', error.response?.status);
      if (error.response?.status === 401) {
        try {
          clearAuthData();
        } catch (e) {
          console.warn('Error clearing auth data:', e?.message || e);
        }
        setToken(null);
      }
      setUser(null);
      setIsAuthenticated(false);
      return false;
    } finally {
      setLoading(false);
      checkingAuth.current = false;
    }
  }, [token, user, getApi, isAuthenticated]);

  // Run checkAuth only once on mount - intentionally excluding checkAuth from deps to prevent infinite loops
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    checkAuth();
  }, []);

  // Login with Google redirect
  const loginWithGoogle = () => {
    window.location.href = `${API_URL}/api/auth/google`;
  };

  // Process session_id from OAuth callback
  const processSession = async (sessionId) => {
    console.log('Processing OAuth session...');
    setAuthInProgress(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      
      console.log('Session response received');
      
      const newToken = response.data.token;
      const newUser = response.data.user;
      
      // Save to all storages
      saveAuthData(newToken, newUser);
      
      // Update state synchronously to prevent race conditions
      setToken(newToken);
      setUser(newUser);
      setIsAuthenticated(true);
      authValidated.current = true;
      setLoading(false);
      
      // Clear progress for new users (wrapped in try-catch)
      if (response.data.is_new_user) {
        try {
          safeRemoveItem('userProgress');
          safeRemoveItem('tracks');
          safeRemoveItem('lessons');
        } catch (e) {
          console.warn('Error clearing new user progress:', e?.message || e);
        }
      }
      
      return newUser;
    } catch (error) {
      console.error('Session processing error:', error);
      throw error;
    } finally {
      setAuthInProgress(false);
    }
  };

  // Login with email/password
  const loginWithEmail = async (email, password) => {
    console.log('Attempting email login...');
    setAuthInProgress(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      
      console.log('Login response received');
      
      const newToken = response.data.token;
      const newUser = response.data.user;
      
      // Save to all storages
      saveAuthData(newToken, newUser);
      
      // Update state synchronously to prevent race conditions
      setToken(newToken);
      setUser(newUser);
      setIsAuthenticated(true);
      authValidated.current = true;
      setLoading(false);
      
      return response.data;
    } catch (error) {
      console.error('Login error:', error.response?.data);
      throw error;
    } finally {
      setAuthInProgress(false);
    }
  };

  // Register with email/password
  const registerWithEmail = async (email, password, name) => {
    console.log('Attempting registration...');
    setAuthInProgress(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/register`,
        { email, password, name },
        { withCredentials: true }
      );
      
      console.log('Register response received');
      
      const newToken = response.data.token;
      const newUser = response.data.user;
      
      // Save to all storages
      saveAuthData(newToken, newUser);
      
      // Update state synchronously to prevent race conditions
      setToken(newToken);
      setUser(newUser);
      setIsAuthenticated(true);
      authValidated.current = true;
      setLoading(false);
      
      return response.data;
    } catch (error) {
      console.error('Registration error:', error.response?.data);
      throw error;
    } finally {
      setAuthInProgress(false);
    }
  };

  // Logout
  const logout = async () => {
    console.log('Logging out...');
    try {
      const api = getApi();
      await api.post('/api/auth/logout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuthData();
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  // Refresh user data
  const refreshUser = async () => {
    await checkAuth();
  };

  // Select or change course
  const selectCourse = async (courseId) => {
    try {
      const api = getApi();
      const response = await api.post('/api/user/select-course', { course_id: courseId });
      
      // Update interface language based on course
      const interfaceLang = response.data.interface_language || 'pt';
      i18n.changeLanguage(interfaceLang);
      
      // Save course to storage
      safeSetItem(COURSE_KEY, courseId);
      
      // Update user state with new course
      if (user) {
        const updatedUser = { ...user, selected_course: courseId, interface_language: interfaceLang };
        setUser(updatedUser);
        saveAuthData(token, updatedUser);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error selecting course:', error);
      throw error;
    }
  };

  // Set interface language based on user's course
  useEffect(() => {
    if (user?.interface_language) {
      i18n.changeLanguage(user.interface_language);
    }
  }, [user?.interface_language]);

  const value = {
    user,
    loading,
    isAuthenticated,
    authInProgress,
    loginWithGoogle,
    loginWithEmail,
    registerWithEmail,
    processSession,
    logout,
    refreshUser,
    checkAuth,
    setUser,
    selectCourse,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
